import {
  type IGlobalAttributes,
  type IJsonModel,
  Model,
} from 'flexlayout-react'

// Floor pane sizes so no splitter drag can crush a pane into an unrecoverable
// sliver or push its content off the viewport (LG-227). The vendored FlexLayout
// enforces these both when clamping splitter drags (RowNode.getSplitterBounds /
// calculateSplit) and as CSS min-width/min-height on rendered panes. Folded
// tabsets are exempt: their calculated minimum collapses to the fixed 36px bar
// (TabSetNode.calcMinMaxSize), so the floor never fights the fold feature.
// The effective on-screen minimum height is this value plus the tab strip
// (~36px), which FlexLayout adds on top of the attribute.
export const TABSET_MIN_WIDTH = 150
export const TABSET_MIN_HEIGHT = 80

/**
 * Global attributes shared by every preset. Kept in one place so the pane-size
 * floor and chrome behavior can't drift between presets.
 */
const SHARED_GLOBAL_ATTRS = {
  tabEnableClose: false,
  tabEnableRename: false,
  tabSetEnableClose: false,
  tabSetEnableMaximize: true,
  tabEnableRenderOnDemand: false,
  splitterSize: 4,
  tabSetMinWidth: TABSET_MIN_WIDTH,
  tabSetMinHeight: TABSET_MIN_HEIGHT,
} satisfies IGlobalAttributes

// --- Auth-only tab IDs ---
const AUTH_TAB_IDS = ['attempts-tab', 'notes-tab']

const AUTH_TAB_DEFINITIONS = [
  { id: 'attempts-tab', name: 'Attempts', component: 'attempts' },
  { id: 'notes-tab', name: 'Note', component: 'notes' },
]

function buildBorders() {
  return [
    {
      type: 'border' as const,
      location: 'left',
      enableAutoHide: true,
      selected: -1,
      size: 400,
      children: [],
    },
    {
      type: 'border' as const,
      location: 'top',
      enableAutoHide: true,
      selected: -1,
      size: 300,
      children: [],
    },
    {
      type: 'border' as const,
      location: 'bottom',
      enableAutoHide: true,
      selected: -1,
      size: 250,
      children: [],
    },
    {
      type: 'border' as const,
      location: 'right',
      enableAutoHide: true,
      selected: -1,
      size: 420,
      children: [],
    },
  ]
}

// --- Preset definitions ---

interface PresetLayout {
  id: string
  name: string
  buildLayout: (isSignedIn: boolean) => IJsonModel
}

/**
 * Build the default editor layout.
 * Left tabset (45%) | Right column: Code (60%) over Tests (40%).
 */
function buildDefaultLayout(isSignedIn: boolean): IJsonModel {
  const leftTabs = [
    {
      type: 'tab' as const,
      id: 'description-tab',
      name: 'Description',
      component: 'description',
      enableClose: false,
    },
    {
      type: 'tab' as const,
      id: 'editorial-tab',
      name: 'Editorial',
      component: 'editorial',
      enableClose: false,
    },
    ...(isSignedIn
      ? [
          {
            type: 'tab' as const,
            id: 'attempts-tab',
            name: 'Attempts',
            component: 'attempts',
            enableClose: false,
          },
          {
            type: 'tab' as const,
            id: 'notes-tab',
            name: 'Note',
            component: 'notes',
            enableClose: false,
          },
        ]
      : []),
  ]

  return {
    global: { ...SHARED_GLOBAL_ATTRS },
    borders: buildBorders(),
    layout: {
      type: 'row',
      weight: 100,
      children: [
        {
          type: 'tabset',
          id: 'LEFT_PANE',
          weight: 45,
          selected: 0,
          children: leftTabs,
        },
        {
          type: 'column',
          id: 'RIGHT_COLUMN',
          weight: 55,
          children: [
            {
              type: 'tabset',
              id: 'CODE_PANE',
              weight: 60,
              selected: 0,
              children: [
                {
                  type: 'tab',
                  id: 'code-editor-tab',
                  name: 'Code',
                  component: 'code',
                  enableClose: false,
                },
              ],
            },
            {
              type: 'tabset',
              id: 'TEST_PANE',
              weight: 40,
              selected: 0,
              children: [
                {
                  type: 'tab',
                  id: 'test-cases-tab',
                  name: 'Test Cases',
                  component: 'test-cases',
                  enableClose: false,
                },
                {
                  type: 'tab',
                  id: 'test-results-tab',
                  name: 'Test Results',
                  component: 'test-results',
                  enableClose: false,
                },
              ],
            },
          ],
        },
      ],
    },
  } as unknown as IJsonModel
}

/**
 * Build the note-taking layout.
 * Section 1 (35%): Description/Editorial/Attempts
 * Section 2 (35%): Code editor (full height)
 * Section 3 (30%): Note (top 50%) + Test Cases & Test Results (bottom 50%)
 */
export function buildNoteTakingLayout(isSignedIn: boolean): IJsonModel {
  const leftTabs = [
    {
      type: 'tab' as const,
      id: 'description-tab',
      name: 'Description',
      component: 'description',
      enableClose: false,
    },
    {
      type: 'tab' as const,
      id: 'editorial-tab',
      name: 'Editorial',
      component: 'editorial',
      enableClose: false,
    },
    ...(isSignedIn
      ? [
          {
            type: 'tab' as const,
            id: 'attempts-tab',
            name: 'Attempts',
            component: 'attempts',
            enableClose: false,
          },
        ]
      : []),
  ]

  return {
    global: { ...SHARED_GLOBAL_ATTRS },
    borders: buildBorders(),
    layout: {
      type: 'row',
      weight: 100,
      children: [
        {
          type: 'tabset',
          id: 'LEFT_PANE',
          weight: 35,
          selected: 0,
          children: leftTabs,
        },
        {
          type: 'tabset',
          id: 'CODE_PANE',
          weight: 35,
          selected: 0,
          children: [
            {
              type: 'tab',
              id: 'code-editor-tab',
              name: 'Code',
              component: 'code',
              enableClose: false,
            },
          ],
        },
        // Only include right column when signed in (avoid empty tabset)
        ...(isSignedIn
          ? [
              {
                type: 'column' as const,
                id: 'RIGHT_COLUMN',
                weight: 30,
                children: [
                  {
                    type: 'tabset' as const,
                    id: 'NOTE_PANE',
                    weight: 50,
                    selected: 0,
                    children: [
                      {
                        type: 'tab',
                        id: 'notes-tab',
                        name: 'Note',
                        component: 'notes',
                        enableClose: false,
                      },
                    ],
                  },
                  {
                    type: 'tabset' as const,
                    id: 'TEST_PANE',
                    weight: 50,
                    selected: 0,
                    children: [
                      {
                        type: 'tab',
                        id: 'test-cases-tab',
                        name: 'Test Cases',
                        component: 'test-cases',
                        enableClose: false,
                      },
                      {
                        type: 'tab',
                        id: 'test-results-tab',
                        name: 'Test Results',
                        component: 'test-results',
                        enableClose: false,
                      },
                    ],
                  },
                ],
              },
            ]
          : [
              {
                type: 'column' as const,
                id: 'RIGHT_COLUMN',
                weight: 30,
                children: [
                  {
                    type: 'tabset' as const,
                    id: 'TEST_PANE',
                    weight: 100,
                    selected: 0,
                    children: [
                      {
                        type: 'tab',
                        id: 'test-cases-tab',
                        name: 'Test Cases',
                        component: 'test-cases',
                        enableClose: false,
                      },
                      {
                        type: 'tab',
                        id: 'test-results-tab',
                        name: 'Test Results',
                        component: 'test-results',
                        enableClose: false,
                      },
                    ],
                  },
                ],
              },
            ]),
      ],
    },
  } as unknown as IJsonModel
}

export const PRESET_LAYOUTS: PresetLayout[] = [
  { id: 'default', name: 'Default', buildLayout: buildDefaultLayout },
  {
    id: 'note-taking',
    name: 'Note-taking',
    buildLayout: buildNoteTakingLayout,
  },
]

/**
 * Get a preset layout by ID. Returns null if not a preset.
 */
export function getPresetLayout(
  id: string,
  isSignedIn: boolean,
): IJsonModel | null {
  const preset = PRESET_LAYOUTS.find(
    (p) => p.id === (id === 'interview' ? 'default' : id),
  )
  return preset ? preset.buildLayout(isSignedIn) : null
}

// --- localStorage helpers ---

export function getLayoutStorageKey(userId: string): string {
  return `beyond-ctci-editor-layout-${userId}`
}

export function storeLayout(userId: string, model: IJsonModel): void {
  try {
    localStorage.setItem(getLayoutStorageKey(userId), JSON.stringify(model))
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

export function getStoredLayout(userId: string): IJsonModel | null {
  try {
    const raw = localStorage.getItem(getLayoutStorageKey(userId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Basic structural check
    if (parsed && typeof parsed === 'object' && parsed.layout) {
      return normalizeRetiredLayout(parsed as IJsonModel)
    }
    return null
  } catch {
    return null
  }
}

export function clearStoredLayout(userId: string): void {
  try {
    localStorage.removeItem(getLayoutStorageKey(userId))
    localStorage.removeItem(`beyond-ctci-active-layout-id-${userId}`)
  } catch {
    // localStorage unavailable — silently fail
  }
}

/**
 * Store the activeLayoutId that was in effect when the layout was last saved/applied.
 * Used to detect cross-session staleness: if the DB's activeLayoutId differs from
 * this stored value, localStorage is stale and should be replaced.
 */
export function storeActiveLayoutId(userId: string, layoutId: string): void {
  try {
    localStorage.setItem(`beyond-ctci-active-layout-id-${userId}`, layoutId)
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

export function getStoredActiveLayoutId(userId: string): string | null {
  try {
    return localStorage.getItem(`beyond-ctci-active-layout-id-${userId}`)
  } catch {
    return null
  }
}

/**
 * Validate that an IJsonModel can be parsed by FlexLayout without errors.
 * Returns true if valid, false if corrupted.
 */
export function isValidLayout(json: IJsonModel): boolean {
  try {
    Model.fromJson(json)
    return true
  } catch {
    return false
  }
}

// --- Auth-only tab normalization ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LayoutNode = Record<string, any>

function findAllTabIds(node: LayoutNode): string[] {
  const ids: string[] = []
  if (node.type === 'tab' && node.id) {
    ids.push(node.id)
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      ids.push(...findAllTabIds(child))
    }
  }
  return ids
}

function findTargetTabset(node: LayoutNode): LayoutNode | null {
  // Prefer LEFT_PANE by ID
  if (node.type === 'tabset' && node.id === 'LEFT_PANE') return node
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const found = findTargetTabset(child)
      if (found) return found
    }
  }
  // Fall back to first tabset containing description-tab
  if (node.type === 'tabset' && Array.isArray(node.children)) {
    if (node.children.some((c: LayoutNode) => c.id === 'description-tab')) {
      return node
    }
  }
  return null
}

function isRemovedTab(node: LayoutNode, tabIds: string[]): boolean {
  // Saved layouts are only shape-checked at the top, so a malformed child
  // (null, a primitive) is dropped rather than crashing the walk.
  if (!node || typeof node !== 'object') return true
  return (
    node.type === 'tab' &&
    (node.component === 'ai-interviewer' ||
      node.id === 'ai-interviewer-tab' ||
      tabIds.includes(node.id))
  )
}

function stripTabs(node: LayoutNode, tabIds: string[]): LayoutNode {
  const copy = { ...node }
  if (Array.isArray(copy.children)) {
    const selectedChild = copy.children[copy.selected]
    const survivingChildren = copy.children.filter(
      (child: LayoutNode) => !isRemovedTab(child, tabIds),
    )
    if (copy.type === 'tabset' && typeof copy.selected === 'number') {
      copy.selected = Math.max(0, survivingChildren.indexOf(selectedChild))
    }
    copy.children = survivingChildren
      .map((child: LayoutNode) => stripTabs(child, tabIds))
      // Remove empty tabsets (but not the root)
      .filter(
        (child: LayoutNode) =>
          !(
            child.type === 'tabset' &&
            Array.isArray(child.children) &&
            child.children.length === 0
          ),
      )
  }
  return copy
}

function pruneEmptyContainers(node: LayoutNode): LayoutNode {
  const copy = { ...node }
  if (Array.isArray(copy.children)) {
    copy.children = copy.children
      .map((child: LayoutNode) => pruneEmptyContainers(child))
      .filter((child: LayoutNode) => {
        if (!Array.isArray((child as LayoutNode).children)) return true
        return (child as LayoutNode).children!.length > 0
      })
  }
  return copy
}

function stripBorderTabs(json: LayoutNode, tabIds: string[]): void {
  if (!Array.isArray(json.borders)) return
  for (const border of json.borders) {
    if (Array.isArray(border.children)) {
      const selectedChild =
        typeof border.selected === 'number' && border.selected >= 0
          ? border.children[border.selected]
          : undefined
      border.children = border.children.filter(
        (c: LayoutNode) => !isRemovedTab(c, tabIds),
      )
      // Re-resolve the selection — an out-of-range `selected` after a strip
      // makes FlexLayout throw on load. If the selected tab survived, keep it
      // selected at its new index; if it was stripped, collapse the border.
      if (typeof border.selected === 'number' && border.selected >= 0) {
        border.selected = selectedChild
          ? border.children.indexOf(selectedChild)
          : -1
      }
    }
  }
}

/** Remove retired panes from persisted layouts without changing saved data. */
export function normalizeRetiredLayout(model: IJsonModel): IJsonModel {
  const json = JSON.parse(JSON.stringify(model)) as LayoutNode
  json.layout = pruneEmptyContainers(stripTabs(json.layout, []))
  stripBorderTabs(json, [])
  // A custom layout can consist entirely of retired panes.
  if (!json.layout.children?.length)
    json.layout = buildDefaultLayout(false).layout
  return json as unknown as IJsonModel
}

/** Normalize retired panes and the auth-only tabs for the current viewer. */
export function normalizeAuthTabs(
  model: IJsonModel,
  isSignedIn: boolean,
): IJsonModel {
  const json = normalizeRetiredLayout(model) as unknown as LayoutNode

  // Ensure render-on-demand is disabled so that hidden tabs (like Test Cases)
  // still mount their React components. The Test Cases pane syncs test case
  // state to context which the Run button depends on — if the pane never
  // renders, Run silently fails.
  const global: IGlobalAttributes = json.global ?? {}
  global.tabEnableRenderOnDemand = false

  // Enforce the pane-size floor on stored/custom layouts too — layouts saved
  // before the floor existed (or with it since raised) would otherwise still
  // let a splitter drag crush a pane (LG-227).
  global.tabSetMinWidth = TABSET_MIN_WIDTH
  global.tabSetMinHeight = TABSET_MIN_HEIGHT
  json.global = global

  if (!isSignedIn) {
    json.layout = stripTabs(json.layout, AUTH_TAB_IDS)
    json.layout = pruneEmptyContainers(json.layout)
    stripBorderTabs(json, AUTH_TAB_IDS)
    if (!json.layout.children?.length)
      json.layout = buildDefaultLayout(false).layout
    return json as unknown as IJsonModel
  }

  // Signed in — inject missing auth tabs
  const existingIds = findAllTabIds(json.layout)
  const missingTabs = AUTH_TAB_DEFINITIONS.filter(
    (tab) => !existingIds.includes(tab.id),
  )

  if (missingTabs.length === 0) return json as unknown as IJsonModel

  const target = findTargetTabset(json.layout)
  if (target && Array.isArray(target.children)) {
    for (const tab of missingTabs) {
      target.children.push({
        type: 'tab',
        id: tab.id,
        name: tab.name,
        component: tab.component,
        enableClose: false,
      })
    }
  }

  return json as unknown as IJsonModel
}
